import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import clsx from 'clsx';

interface AuditCheckResult {
  status: 'ok' | 'warning';
  count: number;
  issues: Record<string, unknown>[];
}

type AuditResults = Record<string, AuditCheckResult>;

const CHECK_METADATA: Record<string, { label: string; description: string }> = {
  tokenIntegrity: {
    label: 'Token Integrity',
    description: 'Verifies tokens_used on subscriptions matches SUM of usage_records',
  },
  orderSplit: {
    label: 'Order Split',
    description: 'Checks tokens_from_quota + tokens_overage equals tile_count on orders',
  },
  pricingMismatch: {
    label: 'Pricing Mismatch',
    description: 'Subscriber orders should have total_cents = 0 (no overage billing)',
  },
  overchargedOrders: {
    label: 'Overcharged Orders',
    description: 'Orders with tokens_from_quota=0 but user has active subscription',
  },
  rolloverViolations: {
    label: 'Rollover Violations',
    description: 'Rollover tokens exceeding package rollover limit',
  },
  stripeAlignment: {
    label: 'Stripe Alignment',
    description: 'Active subscriptions with cancel_at_period_end=true (pending cancellations)',
  },
  productStripeSync: {
    label: 'Product-Stripe Sync',
    description: 'Paid packages missing Stripe product or price IDs',
  },
  stripePeriodExpiry: {
    label: 'Stripe Period Expiry',
    description: 'Local current_period_end vs Stripe billing period end (renewal drift)',
  },
  tokenLedgerDrift: {
    label: 'Token Ledger vs Counter',
    description: 'Each live subscription\'s token ledger against today\'s counter, with the likely cause: not filled yet (before the nightly job), period not quarterly, plan changed, or unexplained',
  },
};

function AuditCard({
  name,
  result,
  onRecheck,
  isRechecking,
}: {
  name: string;
  result: AuditCheckResult;
  onRecheck: () => void;
  isRechecking: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const meta = CHECK_METADATA[name];

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/50 overflow-hidden">
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
          {result.status === 'ok' ? (
            <CheckCircleIcon className="h-6 w-6 text-emerald-400 flex-shrink-0" />
          ) : (
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-400 flex-shrink-0" />
          )}
          <div>
            <h3 className="text-sm font-medium text-white">{meta?.label ?? name}</h3>
            <p className="text-xs text-slate-400 mt-0.5">{meta?.description}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={clsx(
              'px-2 py-0.5 rounded-full text-xs font-medium',
              result.status === 'ok'
                ? 'bg-emerald-900/50 text-emerald-300'
                : 'bg-amber-900/50 text-amber-300',
            )}
          >
            {result.status === 'ok' ? 'Pass' : `${result.count} issue${result.count !== 1 ? 's' : ''}`}
          </span>

          <button
            onClick={onRecheck}
            disabled={isRechecking}
            className="p-1 rounded hover:bg-slate-700 disabled:opacity-50 transition-colors"
            title="Re-check"
          >
            <ArrowPathIcon className={clsx('h-4 w-4 text-slate-400', isRechecking && 'animate-spin')} />
          </button>

          {result.issues.length > 0 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1 rounded hover:bg-slate-700 transition-colors"
            >
              {expanded ? (
                <ChevronUpIcon className="h-4 w-4 text-slate-400" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 text-slate-400" />
              )}
            </button>
          )}
        </div>
      </div>

      {expanded && result.issues.length > 0 && (
        <div className="border-t border-slate-700 overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-900/50">
              <tr>
                {Object.keys(result.issues[0]).map((key) => (
                  <th key={key} className="px-3 py-2 text-slate-400 font-medium">
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.issues.map((issue, i) => (
                <tr key={i} className="border-t border-slate-700/50 hover:bg-slate-700/30">
                  {Object.values(issue).map((val, j) => (
                    <td key={j} className="px-3 py-2 text-slate-300 whitespace-nowrap">
                      {String(val ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AdminBillingAuditPage() {
  const queryClient = useQueryClient();
  const [recheckingCheck, setRecheckingCheck] = useState<string | null>(null);

  const {
    data: results,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery<AuditResults>({
    queryKey: ['admin', 'billing', 'audit'],
    queryFn: () => api.adminGetBillingAudit(),
  });

  const handleRunAll = () => {
    refetch();
  };

  const handleRecheck = async (checkName: string) => {
    setRecheckingCheck(checkName);
    try {
      const result = await api.adminGetBillingAuditCheck(checkName);
      queryClient.setQueryData<AuditResults>(['admin', 'billing', 'audit'], (old) =>
        old ? { ...old, [checkName]: result } : old,
      );
    } finally {
      setRecheckingCheck(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-red-700 bg-red-900/20 p-6 text-center">
        <p className="text-red-300">Failed to load billing audit results.</p>
        <button
          onClick={() => refetch()}
          className="mt-3 px-4 py-2 text-sm bg-red-700 hover:bg-red-600 text-white rounded transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const checkNames = Object.keys(CHECK_METADATA);
  const totalIssues = checkNames.reduce((sum, name) => sum + (results?.[name]?.count ?? 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Billing Audit</h1>
          <p className="text-sm text-slate-400 mt-1">
            {totalIssues === 0
              ? 'All checks passed'
              : `${totalIssues} issue${totalIssues !== 1 ? 's' : ''} found`}
          </p>
        </div>
        <button
          onClick={handleRunAll}
          disabled={isFetching}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-lg transition-colors"
        >
          <ArrowPathIcon className={clsx('h-4 w-4', isFetching && 'animate-spin')} />
          Run All Checks
        </button>
      </div>

      <div className="grid gap-4">
        {checkNames.map((name) => {
          const result = results?.[name];
          if (!result) return null;
          return (
            <AuditCard
              key={name}
              name={name}
              result={result}
              onRecheck={() => handleRecheck(name)}
              isRechecking={recheckingCheck === name}
            />
          );
        })}
      </div>
    </div>
  );
}
